import { useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useParams } from "react-router";
import { api } from "../../../convex/_generated/api";
import { isBackendError } from "@repo/types";
import { useCandidateAuthStore } from "./candidateAuthStore";
import { LastNameModal } from "./last-name-modal";

export function AuthGuard() {
  const { id } = useParams<{ id: string }>();
  const { token, setEvaluationId, setToken, setFirstName, setEmail, setParcoursId } =
    useCandidateAuthStore();

  // Local state for the token being validated
  const [currentLastName, setCurrentLastName] = useState<string | undefined>(
    token,
  );
  const [error, setError] = useState<string | undefined>();
  const [blockedUntil, setBlockedUntil] = useState<number | undefined>();

  // Fetch evaluation type without authentication (for modal display)
  const typeResult = useQuery(
    api.evaluations.obtenirTypeEvaluation,
    id ? { evaluationId: id } : "skip",
  );

  // Get evaluation type from the unauthenticated query
  const evaluationType =
    typeResult && "typeEval" in typeResult ? typeResult.typeEval : undefined;

  // Look up parcoursId from evaluationId (no auth required)
  const parcoursLookup = useQuery(
    api.parcoursEvaluations.obtenirParcoursIdDepuisEvaluation,
    id ? { evaluationId: id as any } : "skip",
  );
  const parcoursId = parcoursLookup?.parcoursId;

  // Use the generic evaluation query from evaluations.ts
  const result = useQuery(
    api.evaluations.testConnexion,
    id && currentLastName && parcoursId
      ? { evaluationId: id, parcoursId, token: currentLastName }
      : "skip",
  );

  // Update stored token when validation succeeds
  useEffect(() => {
    if (
      result &&
      "statut" in result &&
      result.statut === "ok" &&
      id &&
      currentLastName &&
      parcoursId
    ) {
      setToken(currentLastName);
      setEvaluationId(id);
      setParcoursId(parcoursId);
      setFirstName(result.prenomCandidat ?? "");
      setEmail(result.emailCandidat ?? "");
      setError(undefined);
      setBlockedUntil(undefined);
    }
  }, [
    result,
    id,
    currentLastName,
    parcoursId,
    setToken,
    setEvaluationId,
    setParcoursId,
    setFirstName,
    setEmail,
  ]);

  // Handle modal submission
  const handleLastNameSubmit = (token: string) => {
    setError(undefined);
    setBlockedUntil(undefined);
    setCurrentLastName(token);
  };

  // No ID in URL
  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Lien invalide</h1>
          <p className="mt-2 text-muted-foreground">
            Le lien d'evaluation est invalide ou incomplet.
          </p>
        </div>
      </div>
    );
  }

  // No token yet - show modal
  if (!currentLastName) {
    return (
      <LastNameModal
        isOpen={true}
        onSubmit={handleLastNameSubmit}
        error={error}
        blockedUntil={blockedUntil}
        evaluationType={evaluationType}
      />
    );
  }

  // Loading state
  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error from query
  if (isBackendError(result)) {
    if (result.erreur === "Évaluation introuvable") {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-destructive">
              Évaluation introuvable
            </h1>
            <p className="mt-2 text-muted-foreground">
              L'évaluation demandée n'existe pas ou a été supprimée.
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <LastNameModal
          isOpen={true}
          onSubmit={handleLastNameSubmit}
          error={result.erreur}
          blockedUntil={result.bloqueJusquA}
          evaluationType={evaluationType}
        />
      );
    }
  }

  return <Outlet />;
}
